import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Paper,
  TablePagination,
  Typography,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CheckIcon from "@material-ui/icons/Check";
import SearchIcon from "@material-ui/icons/Search";
import SortIcon from "@material-ui/icons/Sort";
import instance from "api/globalaxios";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import React, { useEffect, useState } from "react";
// import { formatDate } from "utils";
import CaseDisplay from "./CaseDisplay";
import GeneralActivitiesACCB from "../GeneralActivitiesACCB";
import { format } from "date-fns";
import GlobalSearch from "../Components/GlobalSearch/GlobalSearch";
import ReopenedChip from "../Components/ReopenedChip";
import SearchCaseForm from "../Components/SearchCase";
import { isCaseReopened } from "../utils";

const useStyles = makeStyles((theme) => ({
  box: {
    textAlign: "left",
    margin: "1rem 0 0 0",
    [theme.breakpoints.down("xs")]: {
      padding: "0 0.5rem",
    },
  },
  // Count cards: all three take the row's height, label at the top and the
  // number at the bottom, so the numbers line up even when a label wraps.
  statCell: {
    display: "flex",
  },
  paper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.border}`,
    padding: "1rem 2rem",
    textAlign: "center",
    cursor: "pointer",
    borderRadius: "8px",
    [theme.breakpoints.down("sm")]: {
      padding: "0.5rem 1rem",
    },
    "&:hover": {
      background: "#ddcdb52b",
    },
  },
  selectedPaper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "1rem 2rem",
    textAlign: "center",
    borderRadius: "8px",
    background: "#BDBDBD",
    [theme.breakpoints.down("sm")]: {
      padding: "0.5rem 1rem",
    },
  },
  headingPaper: {
    fontWeight: "500",
    fontSize: "20px",
    color: TEXT.secondary,
    [theme.breakpoints.down("sm")]: {
      fontSize: "15px",
    },
  },
  numberPaper: {
    fontWeight: "500",
    fontSize: "40px",
    color: TEXT.strong,
    [theme.breakpoints.down("sm")]: {
      fontSize: "30px",
    },
  },
  disputeHeading: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: TEXT.primary,
  },
  disputeText: {
    fontSize: "0.875rem",
    fontWeight: 400,
    color: TEXT.primary,
    wordBreak: "break-word",
  },
  listScroll: {
    overflowX: "auto",
  },
  listInner: {
    minWidth: 600,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    flexWrap: "wrap",
    gap: "12px",
  },
  toolbarActions: {
    display: "flex",
    alignItems: "stretch",
    gap: "8px",
  },
  // At least the Search Cases box's 33px. "stretch" only matches it while they
  // share a line; on a phone the buttons wrap onto their own line and, with
  // no vertical padding, shrank to the text height.
  toolbarButton: {
    minHeight: 33,
    padding: "0 14px",
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
  },
  sortItem: {
    minWidth: 200,
    fontSize: "0.875rem",
    justifyContent: "space-between",
  },
  sortCheck: {
    marginLeft: theme.spacing(2),
  },
}));

const PAGE_SIZE = 10;

// Maps each dashboard tab to its API `type` value, label and stats count field.
const TABS = [
  { key: "registered", label: "Total Cases", countKey: "cases_registered" },
  { key: "pending", label: "Registered Cases", countKey: "cases_pending" },
  { key: "closed", label: "Disposal", countKey: "cases_closed" },
];

const VALID_CHOICES = TABS.map((t) => t.key);

export default function Overview({ profile }) {
  const classes = useStyles();

  const cid = sessionStorage.getItem("bac_overview_caseid");
  const [openCase, setOpenCase] = useState(cid ? true : false);
  const [caseId, setCaseId] = useState(cid ? cid : null);

  const storedChoice = sessionStorage.getItem("bac_overview_choice");
  const initialChoice = VALID_CHOICES.includes(storedChoice)
    ? storedChoice
    : null;
  const [choice, setChoice] = useState(initialChoice);

  // Counts from /bac/dashboard/stats/
  const [stats, setStats] = useState({
    cases_registered: 0,
    cases_pending: 0,
    cases_closed: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Cases (paginated) from /bac/cases/?type=<type>
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [search, setSearch] = useState("");

  const [activities, setActivities] = useState([]);

  const splitCaseName = (name) => {
    if (!name) return ["", ""];
    for (const sep of [" vs. ", " VS ", " vs "]) {
      const idx = name.indexOf(sep);
      if (idx !== -1) return [name.slice(0, idx), name.slice(idx + sep.length)];
    }
    return [name, ""];
  };

  async function getStats() {
    return await instance.get("/bac/dashboard/stats/");
  }

  async function getCases(type, pageNum = 1, searchText = "") {
    return await instance.get("/bac/cases/", {
      params: { type, page: pageNum, page_size: PAGE_SIZE, search: searchText },
    });
  }

  function loadStats() {
    setLoadingStats(true);
    getStats()
      .then((res) => {
        setStats({
          cases_registered: res.data?.cases_registered ?? 0,
          cases_pending: res.data?.cases_pending ?? 0,
          cases_closed: res.data?.cases_closed ?? 0,
        });
        setLoadingStats(false);
      })
      .catch((err) => {
        console.log(err);
        setLoadingStats(false);
      });
  }

  function loadCases(type, pageNum = 1, searchText = search) {
    if (!type) return;
    setLoadingCases(true);
    getCases(type, pageNum, searchText)
      .then((res) => {
        setCases(res.data?.results ?? []);
        setTotalCount(res.data?.count ?? 0);
        setHasNext(Boolean(res.data?.next));
        setHasPrev(Boolean(res.data?.previous));
        setPage(pageNum);
        setLoadingCases(false);
      })
      .catch((err) => {
        console.log(err);
        setLoadingCases(false);
      });
  }

  // Re-fetch everything currently on screen (used after returning from CaseDisplay).
  const reloadOverview = () => {
    loadStats();
    if (choice) loadCases(choice, page, search);
  };

  useEffect(() => {
    loadStats();
    if (initialChoice) loadCases(initialChoice, 1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabClick = (key) => {
    setChoice(key);
    sessionStorage.setItem("bac_overview_choice", key);
    setSortby("");
    loadCases(key, 1, search);
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    loadCases(choice, 1, val);
  };

  // Open a case in CaseDisplay, from the list or the case-number search.
  // The id goes in BEFORE the view switches: after an await (the Find Case
  // search) React 16 renders each setState on its own, and CaseDisplay loads
  // its case once on mount — opening first mounted it with caseId null and
  // fetched /tiacrvdisputedetailsbyid/null/.
  const openCaseView = (id) => {
    sessionStorage.setItem("bac_overview_caseid", id);
    setCaseId(id);
    setOpenCase(true);
  };

  // ---- Sorting (applies to the currently loaded page) ----
  const [sortby, setSortby] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  function sortCases(comparator) {
    setAnchorEl(null);
    setCases((prev) => [...prev].sort(comparator));
  }
  function handleIdSort() {
    setSortby("id");
    sortCases((a, b) => a.id - b.id);
  }
  function handleCNameSort() {
    setSortby("cname");
    sortCases((a, b) => {
      const nameA = splitCaseName(a.name)[0].trim().toUpperCase();
      const nameB = splitCaseName(b.name)[0].trim().toUpperCase();
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });
  }
  function handleRNameSort() {
    setSortby("rname");
    sortCases((a, b) => {
      const nameA = splitCaseName(a.name)[1].trim().toUpperCase();
      const nameB = splitCaseName(b.name)[1].trim().toUpperCase();
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });
  }

  const SORT_OPTIONS = [
    { key: "id", label: "ID", run: handleIdSort },
    { key: "cname", label: "Claimant Name", run: handleCNameSort },
    { key: "rname", label: "Respondent Name", run: handleRNameSort },
  ];
  const sortLabel = SORT_OPTIONS.find((o) => o.key === sortby)?.label;

  // "Find Case": look a case up by type, number and year and open it.
  const [findCaseOpen, setFindCaseOpen] = useState(false);

  if (openCase) {
    return (
      <CaseDisplay
        setOpenCase={setOpenCase}
        caseId={caseId}
        mode={"RV"}
        profile={profile}
        reloadOverview={reloadOverview}
      />
    );
  }

  if (loadingStats) {
    return <CircularCenteredLoader />;
  }

  return (
    <>
      <Dialog
        open={findCaseOpen}
        onClose={() => setFindCaseOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Find Case</DialogTitle>
        <DialogContent>
          <SearchCaseForm
            buttonLabel="Open Case"
            handleSearchSuccess={(caseData) => {
              if (!caseData?.id) return;
              setFindCaseOpen(false);
              openCaseView(caseData.id);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFindCaseOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Grid container justify="center">
        <Grid item md={10} sm={11} xs={12}>
          <Box className={classes.box}>
            {/* Once a tile is picked: Search Cases on the left; Find Case and
                Sort by on the right. */}
            {choice && (
              <Box className={classes.toolbar}>
                <Box>
                  <GlobalSearch
                    value={search}
                    onSearchChange={handleSearchChange}
                    placeholder="General Search..."
                  />
                </Box>
                <Box className={classes.toolbarActions}>
                  <Button
                    variant="outlined"
                    color="primary"
                    className={classes.toolbarButton}
                    startIcon={<SearchIcon fontSize="small" />}
                    onClick={() => setFindCaseOpen(true)}
                  >
                    Find Case
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    className={classes.toolbarButton}
                    startIcon={<SortIcon fontSize="small" />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleClick}
                    aria-controls="overview-sort-menu"
                    aria-haspopup="true"
                  >
                    {sortLabel ? `Sort: ${sortLabel}` : "Sort by"}
                  </Button>
                  <Menu
                    id="overview-sort-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.key}
                        selected={sortby === option.key}
                        onClick={option.run}
                        className={classes.sortItem}
                      >
                        {option.label}
                        {sortby === option.key && (
                          <CheckIcon fontSize="small" className={classes.sortCheck} />
                        )}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Box>
            )}

            <Box height="1rem" />

            {/* ---- Count cards (from /bac/dashboard/stats/) ---- */}
            <Grid container spacing={3}>
              {TABS.map((tab) => (
                <Grid item sm={4} xs={6} key={tab.key} className={classes.statCell}>
                  <Paper
                    className={
                      choice === tab.key
                        ? classes.selectedPaper
                        : classes.paper
                    }
                    onClick={() => handleTabClick(tab.key)}
                  >
                    <Typography className={classes.headingPaper}>
                      {tab.label}
                    </Typography>
                    <Typography className={classes.numberPaper}>
                      {stats[tab.countKey]}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box height="2rem" />

            {choice ? (
              <Grid container>
                <Grid item md={12} sm={12} xs={12}>
                  {/* Same column widths at every size (they add up to 12); on a
                      phone the list scrolls sideways instead. Quarter-width
                      columns there added up to 15/12 and wrapped Stage under ID. */}
                  <Box className={classes.listScroll}>
                    <Box className={classes.listInner}>
                      <Box style={{ padding: "1rem" }}>
                        <Grid container justify="flex-start" spacing={1}>
                          <Grid item xs={2}>
                            <Typography className={classes.disputeHeading}>
                              ID
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography className={classes.disputeHeading}>
                              Claimant Name
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography className={classes.disputeHeading}>
                              Respondent Name
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography className={classes.disputeHeading}>
                              Start Date
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography className={classes.disputeHeading}>
                              Stage
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      <Box height="0.4rem" />

                      {loadingCases ? (
                        <CircularCenteredLoader />
                      ) : cases.length === 0 ? (
                        <Box style={{ padding: "1rem" }}>
                          <Typography className={classes.disputeText}>
                            No cases found.
                          </Typography>
                        </Box>
                      ) : (
                        cases.map((dispute) => {
                          const [complainant, respondent] = splitCaseName(dispute.name).map(s => s.trim());
                          return (
                            <Box key={dispute.id}>
                              <Paper
                                style={{ padding: "1rem", cursor: "pointer" }}
                                onClick={() => openCaseView(dispute.id)}
                              >
                                <Grid container justify="flex-start" spacing={1}>
                                  <Grid item xs={2}>
                                    <Typography className={classes.disputeText}>
                                      {dispute.case_number || dispute.id}
                                    </Typography>
                                    {isCaseReopened(dispute) && (
                                      <ReopenedChip
                                        reopenDate={dispute.reopen_date}
                                        reopenReason={dispute.reopen_reason}
                                        style={{ marginTop: "4px" }}
                                      />
                                    )}
                                  </Grid>
                                  <Grid item xs={3}>
                                    <Typography className={classes.disputeText}>
                                      {complainant}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={2}>
                                    <Typography className={classes.disputeText}>
                                      {respondent}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={2}>
                                    <Typography className={classes.disputeText}>
                                      {dispute.created}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={3}>
                                    <Typography className={classes.disputeText}>
                                      {dispute?.stage || "N/A"}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Paper>
                              <Box height="0.6rem" />
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Box>

                  {/* ---- Pagination ---- */}
                  {!loadingCases && totalCount > 0 && (
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page - 1}
                      onChangePage={(_, newPage) => loadCases(choice, newPage + 1, search)}
                      rowsPerPage={PAGE_SIZE}
                      rowsPerPageOptions={[]}
                    />
                  )}
                </Grid>
              </Grid>
            ) : (
              <Grid container>
                <Grid item xs={12}>
                  <GeneralActivitiesACCB
                    activities={activities}
                    setActivities={setActivities}
                    activityTitle={"Account Activity"}
                  />
                  <Box height="2rem" />
                </Grid>
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
